import React from "react";

import { AlertV4, ButtonV4, ModalV4, SectionSpinner, SelectV4 } from "../common/ui";
import { anyBusy } from "../common/ui/SectionSpinnerV4";
import GalleryCameraV4 from "./GalleryCameraV4";

import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  GALLERY_AUTHOR,
  GALLERY_COLORS,
  GALLERY_TYPES,
  GALLERY_WIDTHS,
  MOCK_GALLERY_HISTORY,
  THUMB_SCALE,
  clampPoint,
  galleryType,
  strokeSegments,
  timeOfIso,
  timeOfNow,
  type GalleryPhoto,
  type Segment,
  type Stroke,
} from "./mockPatientV4";
import {
  annotationStrokesOf,
  galleryStrokesToAnnotationItems,
  errorTextOf,
  normalizeExamV4Slice,
  galleryUploadDescription,
  galleryViewsFromApi,
  type ExamV4Slice,
  type GalleryPhotoView,
} from "./examModelV4";
import { dataUriFromImageUrl } from "./galleryRasterV4";
import { CARD_NAMES } from "./examDispatchV4";
/* practice ของทีม: card import ชื่อ sequence + ACTIONS จากไฟล์ sequence (ดู CardOrdersV4.tsx:8-15) */
import {
  ACTIONS,
  SEQUENCE_NAME,
  type ExamRunParamsLoose,
  type ExamRunSequenceProp,
} from "../common/sequence/ExamV4";
import type { PatientMenuKey } from "./PatientPanelV4";

/**
 * Image Gallery (6c ของหน้าตรวจรักษา) ตามแบบ PatientGallery.dc.html
 * โครงและ style ทั้งหมดถอดจาก markup ที่ design เรนเดอร์จริง
 * ข้อมูลยังเป็นตัวอย่าง ยังไม่ต่อ API และยังไม่มีการอัปโหลดไฟล์จริง
 */

export type CardGalleryV4Props = {
  encounterNumber?: string;
  doctorName?: string;
  onSelectMenu: (menu: Extract<PatientMenuKey, "doctor" | "gallery" | "nursing">) => void;
  /** slice ของ ExamV4 sequence (live mode) */
  exam?: Partial<ExamV4Slice> | null;
  /** ตาม practice ของทีม: card เรียก sequence ของ MainHIS (ดู CardOrdersV4.tsx:29-31) */
  runSequence?: ExamRunSequenceProp;
  /** loading ต่อ action: key "<card>_<ACTION>" (BLC_STATUS ของ CommonInterface) */
  buttonLoadCheck?: Record<string, any>;
  /** error ของ sequenceนี้: key = ชื่อ card (SetErrorMessage ของ CommonInterface) */
  errorMessage?: Record<string, any>;
  /** ปิดข้อความ error (AlertV4 onClose) เขียน new ลง state path */
  setProp?: (key: string, value: unknown) => void;
};

const segmentStyle = (segment: Segment): React.CSSProperties => {
  if (segment.kind === "dot") {
    return {
      position: "absolute",
      left: segment.left,
      top: segment.top,
      width: segment.size,
      height: segment.size,
      borderRadius: "50%",
      background: segment.color,
      pointerEvents: "none",
    };
  }
  return {
    position: "absolute",
    left: segment.left,
    top: segment.top,
    width: segment.length,
    height: segment.thickness,
    background: segment.color,
    borderRadius: segment.thickness / 2,
    transformOrigin: "0 50%",
    transform: `rotate(${segment.angle.toFixed(2)}deg)`,
    pointerEvents: "none",
  };
};

/** ขนาด canvas ที่ใช้วาด/อ่าน layer (86630: backend เก็บ points เป็นค่าสัมพัทธ์ 0..1) */
const GALLERY_CANVAS = { width: CANVAS_WIDTH, height: CANVAS_HEIGHT };

/** design แสดงเวลาสั้น (HH:MM) ในรูปของ Encounter วันนี้ */
const stampOf = (at: string): string => (at.length <= 5 ? `วันนี้ ${at}` : at);

const PhotoCanvas: React.FC<{
  photo: GalleryPhoto;
  scale: number;
  strokes?: Stroke[];
}> = ({ photo, scale, strokes = photo.strokes }) => {
  const type = galleryType(photo.label);
  return (
    <span
      className="hv4-photo-canvas"
      style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT, transform: `scale(${scale})` }}
    >
      <span
        className="hv4-photo-base"
        style={{ background: `linear-gradient(135deg, ${type.gradientFrom}, ${type.gradientTo})` }}
      >
        {photo.image ? (
          <img className="hv4-photo-img" src={photo.image} alt={photo.caption} />
        ) : (
          type.emoji
        )}
      </span>
      {strokeSegments(strokes).map((segment) => (
        <span key={segment.key} style={segmentStyle(segment)} />
      ))}
    </span>
  );
};

const PhotoTile: React.FC<{
  photo: GalleryPhoto;
  editable: boolean;
  ariaLabel: string;
  onOpen: () => void;
  /** live mode เท่านั้น: ปุ่มลบติดกับภาพแต่ละใบ (mock ไม่ส่ง -> หน้าตาเท่าน design) */
  onDelete?: () => void;
}> = ({ photo, editable, ariaLabel, onOpen, onDelete }) => (
  <div className="hv4-photo-tile">
    <button type="button" className="hv4-photo-thumb" aria-label={ariaLabel} onClick={onOpen}>
      <PhotoCanvas photo={photo} scale={THUMB_SCALE} />
    </button>
    <b>{photo.label}</b>
    <span>{photo.caption}</span>
    <span className="hv4-photo-meta">
      {stampOf(photo.at)} · {photo.by}
      {photo.editedAt ? " · แก้ไขเส้นแล้ว" : ""}
    </span>
    {editable ? (
      <ButtonV4 className="hv4-btn-photo-edit" onClick={onOpen}>
        ✏ แก้ไขภาพ
      </ButtonV4>
    ) : null}
    {onDelete ? (
      <ButtonV4 variant="danger-ghost" size="sm" className="hv4-btn-photo-del" onClick={onDelete}>
        ลบภาพ
      </ButtonV4>
    ) : null}
  </div>
);

export const CardGalleryV4: React.FC<CardGalleryV4Props> = ({
  encounterNumber,
  doctorName,
  onSelectMenu,
  exam,
  runSequence,
  buttonLoadCheck,
  errorMessage,
  setProp,
}) => {
  const examSlice = exam === undefined || exam === null ? null : normalizeExamV4Slice(exam);
  const sliceIds = examSlice?.ids ?? {};
  const live = !!examSlice && !!examSlice.ids.emrId;
  const galleryTypes = GALLERY_TYPES.map((type) => type.value);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const [photos, setPhotos] = React.useState<GalleryPhoto[]>([]);
  const [photoType, setPhotoType] = React.useState(GALLERY_TYPES[0].value);
  const [caption, setCaption] = React.useState("");
  const [showHistory, setShowHistory] = React.useState(false);
  /** modal ถ่ายภาพด้วยกล้อง (live เท่านั้น — V3 ใช้ WebcamComponent) */
  const [cameraOpen, setCameraOpen] = React.useState(false);
  /** modal เลือก รูปแบบภาพ (template) — live เท่านั้น (V3: ModalTemplateFile) */
  const [templateOpen, setTemplateOpen] = React.useState(false);

  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
  const [editingPhoto, setEditingPhoto] = React.useState<GalleryPhoto | null>(null);
  const [strokes, setStrokes] = React.useState<Stroke[]>([]);
  const [currentStroke, setCurrentStroke] = React.useState<Stroke | null>(null);
  const [drawing, setDrawing] = React.useState(false);
  const [captionDraft, setCaptionDraft] = React.useState("");
  const [color, setColor] = React.useState(GALLERY_COLORS[0]);
  const [lineWidth, setLineWidth] = React.useState(GALLERY_WIDTHS[0].width);
  /** กัน error ตอนรวมเส้นกับภาพ (เกิดบน browser เท่านั้น ไม่เกี่ยวกับ API) */
  const [localError, setLocalError] = React.useState<string | null>(null);
  /** ภาพที่ลบไปในรอบนี้ (soft delete) — V3 เก็บ undoState แล้ว restore (CardImageGallery.tsx:171-179,363-371) */
  const [deletedPhotos, setDeletedPhotos] = React.useState<{ id: number; caption: string }[]>([]);
  /** 86630: ชั้นเส้นที่ลบใน modal นี้ (backend soft delete) กันไม่ให้ overlay แสดงค่าเก่าจนกว่า slice จะอ่านกลับ */
  const [removedAnnotationIds, setRemovedAnnotationIds] = React.useState<number[]>([]);
  const canvasRef = React.useRef<HTMLSpanElement | null>(null);

  /* live: ภาพของ encounter นี้จาก GET /apis/DPO/medical_record/gallery/{emr}/ */
  const galleryKey = examSlice
    ? `${sliceIds.emrId ?? ""}|${examSlice.savedAt ?? ""}|${examSlice.photos.map((photo) => photo.id).join(",")}`
    : "";

  React.useEffect(() => {
    if (!examSlice || !examSlice.ids.emrId) return;
    setPhotos(
      galleryViewsFromApi(examSlice.photos, { types: galleryTypes, doctorName, timeOf: timeOfIso, canvas: GALLERY_CANVAS }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryKey]);

  const readImageFile = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(`${reader.result ?? ""}`);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const cardName = CARD_NAMES.gallery;
  const dispatch = (params: ExamRunParamsLoose): void => {
    runSequence?.(params);
  };
  const busy = (action: string): boolean =>
    buttonLoadCheck?.[`${cardName}_${action}`] === "LOADING";
  const loadError = (): string | null => errorTextOf(errorMessage?.[cardName]);
  /* icon หมุนท้ายชื่อหัวข้อตาม buttonLoadCheck จริง (เหมือนหน้าคัดกรอง) */
  const gallerySectionLoading = anyBusy(busy, [
    ACTIONS.LOAD_GALLERY,
    ACTIONS.UPLOAD_PHOTO,
    ACTIONS.UPDATE_PHOTO,
    ACTIONS.DELETE_PHOTO,
    ACTIONS.RESTORE_PHOTO,
    ACTIONS.LOAD_GALLERY_TEMPLATES,
  ]);
  const historySectionLoading = busy(ACTIONS.LOAD_GALLERY_HISTORY);

  const dispatchReload = () =>
    dispatch({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.LOAD_GALLERY,
      card: cardName,
      ...sliceIds,
    });

  const dispatchGallery = (
    action:
      | "UPLOAD_PHOTO"
      | "UPDATE_PHOTO"
      | "DELETE_PHOTO"
      | "RESTORE_PHOTO"
      | "LOAD_GALLERY_TEMPLATES"
      | "LOAD_GALLERY_HISTORY"
      /* 86630: ชั้นเส้นที่วาดบนภาพ (backend เก็บแยกจากไฟล์ภาพ) */
      | "SAVE_PHOTO_ANNOTATIONS"
      | "DELETE_PHOTO_ANNOTATIONS",
    fields: { [key: string]: unknown } = {},
  ) => dispatch({ sequence: SEQUENCE_NAME, action, card: cardName, ...sliceIds, ...fields });

  /* 86631: ประวัติภาพของ Encounter อื่น อ่านจริงเมื่อเปิดส่วน History (ครั้งแรกเท่านั้น)
     sequence: GET /apis/DPO/encounter-history/?patient= แล้ว GET gallery ของแต่ละ emr (จำกัด 10 encounter) */
  const historyLoadedFor = React.useRef<string | null>(null);
  const toggleHistory = (): void => {
    const next = !showHistory;
    setShowHistory(next);
    if (!live || !next) return;
    const key = `${sliceIds.patientId ?? ""}|${sliceIds.encounterId ?? ""}`;
    if (historyLoadedFor.current === key) return;
    historyLoadedFor.current = key;
    dispatchGallery("LOAD_GALLERY_HISTORY");
  };

  const historyGroups: { encounter: string; at: string; photos: GalleryPhoto[] }[] = live
    ? (examSlice?.galleryHistory ?? []).map((group) => ({
        encounter: group.encounterNumber,
        at: group.dateText,
        photos: galleryViewsFromApi(group.photos, { types: galleryTypes, doctorName, timeOf: timeOfIso, canvas: GALLERY_CANVAS }),
      }))
    : MOCK_GALLERY_HISTORY;

  /** soft delete + เก็บ id ไว้ในหน้าเพื่อ ย้อนกลับ (V3 undoState) */
  const removePhoto = (photo: GalleryPhoto): void => {
    const photoId = (photo as GalleryPhotoView).id;
    if (photoId === undefined) return;
    dispatchGallery("DELETE_PHOTO", { photoId });
    setDeletedPhotos((current) => current.concat({ id: photoId, caption: photo.caption }));
    if (editingPhoto !== null && (editingPhoto as GalleryPhotoView).id === photoId) closeEditor();
  };

  const restoreLastPhoto = (): void => {
    if (deletedPhotos.length === 0) return;
    const last = deletedPhotos[deletedPhotos.length - 1];
    dispatchGallery("RESTORE_PHOTO", { photoId: last.id });
    setDeletedPhotos((current) => current.slice(0, -1));
  };

  const handleFileChosen = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const picked = event.target.files ? Array.from(event.target.files) : [];
    if (picked.length === 0) return;

    /* API ตอบกลับ 400 ถ้า image ไม่ใช่ไฟล์ภาพประเภท raster (png, jpeg) — svg โดน reject */
    const files = picked.filter((file) => file.type !== "image/svg+xml" && file.type.startsWith("image/"));
    const skipped = picked.length - files.length;
    if (files.length === 0) {
      setLocalError("อัปโหลดได้เฉพาะไฟล์รูปภาพ (png, jpeg) เท่านั้น");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    /* V3 รองรับเลือกหลายภาพพร้อมกัน (handleSaveMultipleNewImage) -> ส่งทีละไฟล์เข้า gallery/{emr}/ */
    const trimmed = caption.trim();
    for (const file of files) {
      const image = await readImageFile(file);
      /* 86629 ข้อ 3.2: ไม่พิมพ์คำอธิบาย -> เก็บเฉพาะประเภทภาพ (การ์ดจะแสดงคำอธิบายเป็น "-")
         helper galleryUploadDescription เป็นตัวกำหนดรูปแบบเดียวกันทั้ง upload / camera */
      dispatchGallery("UPLOAD_PHOTO", {
        image,
        description: galleryUploadDescription(photoType, trimmed),
      });
    }
    if (skipped > 0) setLocalError(`ข้ามไฟล์ที่ไม่ใช่รูปภาพ ${skipped} ไฟล์`);
    setCaption("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /**
   * เลือกรูปแบบของแผนกมาเป็นผู้ป่วย (V3: ModalTemplateFile -> handleSaveMultipleNewImage)
   * ต้องแปลง url -> raster data URI ก่อน เพราะ picture API ไม่รับ url (verify จาก staging แล้ว)
   */
  const applyGalleryTemplate = async (template: {
    id: number;
    name: string;
    image: string;
  }): Promise<void> => {
    const result = await dataUriFromImageUrl(template.image);
    if (!result.ok) {
      setLocalError(result.error);
      return;
    }
    const description =
      template.name === "" ? `Template ${template.id}` : `Template: ${template.name}`;
    dispatchGallery("UPLOAD_PHOTO", { image: result.image, description });
    setTemplateOpen(false);
  };

    /** ชื่อภาพที่ใช้กับทั้ง upload / ถ่ายด้วยกล้อง (V3 ใช้ description ของ MedicalRecordPicture) */
  const photoDescription = (): string => galleryUploadDescription(photoType, caption);

  const addPhoto = (): void => {
    if (live) {
      /* API รับ image เป็น data URI แบบเดียวกับ V3 (CardImageGallery.tsx:373-387) */
      if (fileInputRef.current) fileInputRef.current.click();
      return;
    }

    setPhotos((current) =>
      current.concat({
        label: photoType,
        caption: caption.trim() === "" ? "-" : caption.trim(),
        at: timeOfNow(),
        by: GALLERY_AUTHOR,
        strokes: [],
      }),
    );
    setCaption("");
  };

  const openEditor = (photo: GalleryPhoto, index: number | null): void => {
    setEditingPhoto(photo);
    setCaptionDraft(photo.caption === "-" ? "" : photo.caption);
    setEditingIndex(index);
    /* 86630 ข้อ 3: เส้นที่บันทึกแล้วเป็น layer ของ backend (photo.strokes อ่านมาจาก annotations)
       ช่องทางนี้เก็บเฉพาะ "เส้นใหม่ที่ยังไม่ได้บันทึก" เพื่อไม่ส่ง layer เดิมซ้ำ */
    setStrokes([]);
    setRemovedAnnotationIds([]);
    setCurrentStroke(null);
    setDrawing(false);
  };

  const closeEditor = (): void => {
    setEditingPhoto(null);
    setEditingIndex(null);
    setStrokes([]);
    setCurrentStroke(null);
  };

  /** layer ของภาพที่เปิดอยู่ (จาก slice) โดยไม่นับชั้นที่ลบไปใน modal นี้ */
  const savedAnnotationsOf = (photo: GalleryPhoto | null) => {
    if (photo === null || photo.id === undefined || !examSlice) return [];
    const row = examSlice.photos.find((item) => item.id === photo.id);
    return (row?.annotations ?? []).filter((item) => !removedAnnotationIds.includes(item.id));
  };

  /** 86630 ข้อ 3+4: "ย้อนกลับ" = ลบเส้นล่าสุด (เส้นใหม่ที่ยังไม่บันทึกก่อน layer ที่บันทึกแล้ว) */
  const undoStroke = (): void => {
    if (strokes.length > 0) {
      setStrokes((current) => current.slice(0, -1));
      return;
    }
    if (readOnly) return;
    const saved = savedAnnotationsOf(editingPhoto);
    if (saved.length === 0) return;
    const last = saved[saved.length - 1];
    setRemovedAnnotationIds((current) => current.concat(last.id));
    dispatchGallery("DELETE_PHOTO_ANNOTATIONS", { annotationIds: [last.id] });
  };

  /** 86630 ข้อ 3: "ล้างเส้น" = ลบเส้นทั้งหมด ทั้งที่ยังไม่บันทึกและ layer ที่บันทึกแล้ว */
  const clearStrokes = (): void => {
    setStrokes([]);
    if (readOnly) return;
    const saved = savedAnnotationsOf(editingPhoto);
    if (saved.length === 0) return;
    setRemovedAnnotationIds((current) => current.concat(saved.map((item) => item.id)));
    dispatchGallery("DELETE_PHOTO_ANNOTATIONS", { annotationIds: saved.map((item) => item.id) });
  };

  const saveEditor = async (): Promise<void> => {
    if (live) {
      if (editingPhoto === null) return;
      const photoId = (editingPhoto as GalleryPhotoView).id;
      /* 86630: เก็บเส้นเป็น layer ผ่าน clinical-picture-annotation
         (backend ไม่แตะไฟล์ ClinicalPicture.image - Redmine #86780) */
      if (photoId !== undefined && strokes.length > 0) {
        const items = galleryStrokesToAnnotationItems(
          strokes,
          photoId,
          GALLERY_CANVAS,
          savedAnnotationsOf(editingPhoto).length,
        );
        if (items.length === 0) {
          setLocalError("วาดเส้นไม่ครบ (ต้องมีอย่างน้อย 2 จุดต่อ 1 เส้น)");
          return;
        }
        setLocalError(null);
        dispatchGallery("SAVE_PHOTO_ANNOTATIONS", { photoId, items });
      }
      closeEditor();
      return;
    }

    if (editingIndex === null) return;
    setPhotos((current) =>
      current.map((photo, index) =>
        index === editingIndex ? { ...photo, strokes, editedAt: timeOfNow() } : photo,
      ),
    );
    closeEditor();
  };

  const pointOf = (event: { clientX: number; clientY: number }): [number, number] => {
    const canvas = canvasRef.current;
    if (canvas === null) return [0, 0];
    const rect = canvas.getBoundingClientRect();
    return clampPoint(
      ((event.clientX - rect.left) * CANVAS_WIDTH) / rect.width,
      ((event.clientY - rect.top) * CANVAS_HEIGHT) / rect.height,
    );
  };

  /* ref กัน state เก่า(closure stale) ตอน event หลายตัวถูก batch ในรอบ render เดียว
     ถ้าอ่าน drawing/currentStroke ตรง ๆ จุดที่สองของเส้นจะหาย (เจอใน test) */
  const drawingRef = React.useRef(false);
  const strokeRef = React.useRef<Stroke | null>(null);

  /* วาดด้วย pointer event จึงใช้ได้ทั้งเมาส์และนิ้ว/ปากกาบนแท็บเล็ต (86630 ข้อ 3) */
  const handleDown = (event: React.PointerEvent<HTMLSpanElement>): void => {
    if (editingIndex === null) return;
    const stroke: Stroke = { color, width: lineWidth, points: [pointOf(event)] };
    drawingRef.current = true;
    strokeRef.current = stroke;
    setDrawing(true);
    setCurrentStroke(stroke);
  };

  const handleMove = (event: React.PointerEvent<HTMLSpanElement>): void => {
    const current = strokeRef.current;
    if (!drawingRef.current || current === null) return;
    const stroke: Stroke = { ...current, points: current.points.concat([pointOf(event)]) };
    strokeRef.current = stroke;
    setCurrentStroke(stroke);
  };

  const handleUp = (): void => {
    const stroke = strokeRef.current;
    if (!drawingRef.current || stroke === null) {
      drawingRef.current = false;
      setDrawing(false);
      return;
    }
    setStrokes((current) => current.concat(stroke));
    strokeRef.current = null;
    drawingRef.current = false;
    setCurrentStroke(null);
    setDrawing(false);
  };

  const readOnly = editingIndex === null;
  /* layer ที่บันทึกแล้ว (redraw จาก annotations ของ backend) + เส้นที่กำลังวาดใน modal นี้ */
  const savedStrokes = annotationStrokesOf(savedAnnotationsOf(editingPhoto), GALLERY_CANVAS);
  const editingStrokes =
    currentStroke === null
      ? savedStrokes.concat(strokes)
      : savedStrokes.concat(strokes).concat(currentStroke);

  return (
    <>
      <div className="hv4-grp-tabs">
        <button type="button" className="hv4-grp-tab" onClick={() => onSelectMenu("doctor")}>
          ประวัติ · ตรวจร่างกาย · Dx
        </button>
        <span className="hv4-grp-tab hv4-grp-tab-on" aria-current="page">
          Image Gallery
        </span>
        <button type="button" className="hv4-grp-tab" onClick={() => onSelectMenu("nursing")}>
          บันทึกการพยาบาล
        </button>
      </div>

      <div className="hv4-page-head">
        <h1 className="hv4-h1">Image Gallery</h1>
        <span className="hv4-page-sub">ภาพการตรวจและภาพประกอบ · เก็บเข้าประวัติผู้ป่วย</span>
      </div>

      <section id="sec-img" className="hv4-card">
        <div className="hv4-card-head">
          <h2 className="hv4-card-title">
            Image Gallery (ภาพการตรวจ)
            <SectionSpinner running={gallerySectionLoading} label="กำลังโหลด/บันทึกภาพการตรวจ" />
          </h2>
          <ButtonV4
            variant="ghost"
            aria-pressed={showHistory}
            title="ประวัติภาพของ Encounter อื่น"
            onClick={toggleHistory}
          >
            🕘 History
          </ButtonV4>
        </div>

        {showHistory && (
          <div className="hv4-gal-history">
            <b>
              ประวัติภาพของ Encounter อื่น
              <SectionSpinner running={historySectionLoading} label="กำลังอ่านประวัติภาพของ Encounter อื่น" />
            </b>
            {live && busy("LOAD_GALLERY_HISTORY") && (
              <div className="hv4-modal-note">กำลังอ่านประวัติภาพของ Encounter อื่น...</div>
            )}
            {/* 86631: ภาพทุก Encounter ของ HN เดียวกัน (เว้น Encounter ปัจจุบัน) หัวกลุ่ม "{เลข encounter} · {วันที่}" ใหม่สุดอยู่บน */}
            {historyGroups.map((group) => (
              <div key={group.encounter}>
                <div className="hv4-gal-enc">
                  {group.encounter} · {group.at}
                </div>
                <div className="hv4-photo-row">
                  {group.photos.map((photo) => (
                    <PhotoTile
                      key={`${group.encounter}-${photo.caption}`}
                      photo={photo}
                      editable={false}
                      ariaLabel="เปิดดูภาพ"
                      onOpen={() => openEditor(photo, null)}
                    />
                  ))}
                </div>
              </div>
            ))}
            {live && !busy("LOAD_GALLERY_HISTORY") && historyGroups.length === 0 && (
              <div className="hv4-modal-note">ยังไม่เคยถ่ายภาพใน Encounter อื่น</div>
            )}
          </div>
        )}

        <div className="hv4-photo-row">
          {live && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hv4-file-input"
              aria-label="เลือกไฟล์ภาพเพื่ออัปโหลด"
              onChange={handleFileChosen}
            />
          )}
          {photos.map((photo, index) => (
            <PhotoTile
              key={`${photo.label}-${index}`}
              photo={photo}
              editable={true}
              ariaLabel="เปิดภาพเพื่อดู/แก้ไข"
              onOpen={() => openEditor(photo, index)}
              onDelete={live ? () => removePhoto(photo) : undefined}
            />
          ))}
          {photos.length === 0 && <div className="hv4-empty">ยังไม่มีภาพของ Encounter นี้</div>}
        </div>

        {/* ย้อนกลับการลบภาพ (soft delete) — เก็บ id ไว้ในหน้าเพราะ gallery list ไม่คืนรายการที่ is_active=false */}
        {live && deletedPhotos.length > 0 && (
          <div className="hv4-mt-8">
            <span className="hv4-page-sub">
              ลบภาพไปแล้ว {deletedPhotos.length} รายการในหน้านี้: {deletedPhotos.map((item) => item.caption).join(", ")}
            </span>
            <ButtonV4
              variant="ghost"
              size="sm"
              disabled={busy(ACTIONS.RESTORE_PHOTO)}
              onClick={restoreLastPhoto}
            >
              ย้อนกลับการลบภาพ
            </ButtonV4>
          </div>
        )}

        <div className="hv4-form-grid">
          <div>
            <label className="hv4-label" htmlFor="ph_t">
              ประเภทภาพ
            </label>
            <SelectV4
              id="ph_t"
              options={GALLERY_TYPES.map((type) => ({ value: type.value, text: type.value }))}
              value={photoType}
              onChange={setPhotoType}
            />
          </div>
          <div>
            <label className="hv4-label" htmlFor="ph_c">
              คำอธิบายภาพ
            </label>
            <input
              id="ph_c"
              className="hv4-input"
              placeholder="เช่น หลังส่วนล่าง ด้านขวา"
              value={caption}
              onChange={(event) => setCaption(event.target.value)}
            />
          </div>
          <ButtonV4 variant="primary" onClick={addPhoto}>
            ถ่ายภาพ / อัปโหลด
          </ButtonV4>
          {live && (
            <ButtonV4 variant="ghost" className="hv4-btn-camera" onClick={() => setCameraOpen(true)}>
              📷 ถ่ายด้วยกล้อง
            </ButtonV4>
          )}
          {live && (
            <ButtonV4
              variant="ghost"
              className="hv4-btn-template"
              onClick={() => {
                setTemplateOpen(true);
                dispatchGallery("LOAD_GALLERY_TEMPLATES");
              }}
            >
              🖼 เลือกรูปแบบ
            </ButtonV4>
          )}
        </div>

        {/* ข้อความ loading/error ติดกับปุ่มของหน้านี้ (ไม่ต่อท้ายหน้า) */}
        {live && busy(ACTIONS.LOAD_GALLERY) && (
          <AlertV4 tone="info" className="hv4-mt-8">
            กำลังโหลดภาพของ Encounter นี้…
          </AlertV4>
        )}

        {live && loadError() !== null && (
          <AlertV4
            tone="danger"
            className="hv4-mt-8"
            onClose={() => setProp?.("errorMessage." + cardName, null)}
          >
            อ่านภาพในเวชระเบียนไม่สำเร็จ: {loadError()}
          </AlertV4>
        )}

        {/* error ระดับ card (เลือกไฟล์ไม่ได้ / รวมเส้นลงภาพไม่สำเร็จ) — ตอน modal เปิดจะแสดงใน modal แทน */}
        {localError !== null && editingPhoto === null && (
          <AlertV4 tone="danger" className="hv4-mt-8" onClose={() => setLocalError(null)}>
            {localError}
          </AlertV4>
        )}
      </section>

      {live && templateOpen && (
        <ModalV4 title="เลือกรูปแบบภาพ (Template)" onClose={() => setTemplateOpen(false)}>
          {busy(ACTIONS.LOAD_GALLERY_TEMPLATES) && (
            <AlertV4 tone="info" className="hv4-mt-8">
              กำลังโหลดรูปแบบภาพของแผนก…
            </AlertV4>
          )}

          {examSlice && examSlice.galleryTemplates.length === 0 && !busy(ACTIONS.LOAD_GALLERY_TEMPLATES) && (
            <AlertV4 tone="info" className="hv4-mt-8">
              {loadError() !== null
                ? `อ่านรูปแบบภาพไม่สำเร็จ: ${loadError()}`
                : "ยังไม่มีรูปแบบภาพของแผนกนี้ (backend ยังไม่ส่งรายการ)"}
            </AlertV4>
          )}

          {localError !== null && (
            <AlertV4 tone="danger" className="hv4-mt-8" onClose={() => setLocalError(null)}>
              {localError}
            </AlertV4>
          )}

          <div className="hv4-template-row">
            {(examSlice?.galleryTemplates ?? []).map((template) => (
              <button
                key={template.id}
                type="button"
                className="hv4-template-item"
                onClick={() => applyGalleryTemplate(template)}
              >
                <img className="hv4-template-img" src={template.image} alt={template.name} />
                <span className="hv4-template-name">
                  {template.name === "" ? `Template ${template.id}` : template.name}
                </span>
              </button>
            ))}
          </div>
        </ModalV4>
      )}

      {live && (
        <GalleryCameraV4
          open={cameraOpen}
          caption={photoDescription()}
          onClose={() => setCameraOpen(false)}
          onCapture={(image) => dispatchGallery("UPLOAD_PHOTO", { image, description: photoDescription() })}
        />
      )}

      {editingPhoto !== null && (
        <div className="hv4-modal">
          <section
            className="hv4-card hv4-modal-card"
            role="dialog"
            aria-modal="true"
            aria-label="ภาพ"
          >
            <div className="hv4-card-head">
              <div>
                {/* 86630 ข้อ 1: หัว popup แสดง "{ประเภทภาพ} · {คำอธิบาย}" */}
                <b className="hv4-modal-title">
                  {editingPhoto.label} · {editingPhoto.caption === "-" ? "-" : editingPhoto.caption}
                </b>
                <div className="hv4-photo-meta">
                  {stampOf(editingPhoto.at)} · {editingPhoto.by}
                </div>
              </div>
              <ButtonV4 variant="ghost" onClick={closeEditor}>
                ปิด
              </ButtonV4>
            </div>

            {readOnly && <div className="hv4-modal-note">ดูอย่างเดียว (ภาพของ Encounter อื่น)</div>}

            <div className="hv4-tool-row">
              {GALLERY_COLORS.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`hv4-swatch${value === color ? " hv4-swatch-on" : ""}`}
                  style={{ background: value }}
                  aria-label="สี"
                  onClick={() => setColor(value)}
                />
              ))}
              {GALLERY_WIDTHS.map((option) => (
                <button
                  key={option.width}
                  type="button"
                  className={`hv4-width-pill${option.width === lineWidth ? " hv4-width-pill-on" : ""}`}
                  onClick={() => setLineWidth(option.width)}
                >
                  {option.label}
                </button>
              ))}
              <ButtonV4 variant="ghost" size="sm" onClick={undoStroke}>
                ↶ ย้อนกลับ
              </ButtonV4>
              <ButtonV4 variant="ghost" size="sm" onClick={clearStrokes}>
                ล้างเส้น
              </ButtonV4>
              {live && !readOnly && (
                <span className="hv4-page-sub">
                  ชั้นเส้นบนภาพ {savedAnnotationsOf(editingPhoto).length + strokes.length} ชั้น
                </span>
              )}
            </div>

            <div className="hv4-canvas-scroll">
              <span
                ref={canvasRef}
                className={`hv4-canvas${readOnly ? " hv4-canvas-ro" : ""}`}
                style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
                onPointerDown={handleDown}
                onPointerMove={handleMove}
                onPointerUp={handleUp}
                onPointerLeave={handleUp}
                onPointerCancel={handleUp}
              >
                <PhotoCanvas photo={editingPhoto} scale={1} strokes={editingStrokes} />
              </span>
            </div>

            {/* ภาพของ Encounter อื่น (เปิดจาก History) = ดูอย่างเดียว แก้/ลบไม่ได้ (86631 ข้อ 3) */}
            {live && !readOnly && editingPhoto.id !== undefined && (
              <div className="hv4-mt-12">
                <label className="hv4-label" htmlFor="ph_cap">
                  คำอธิบายภาพ
                </label>
                <input
                  id="ph_cap"
                  className="hv4-input"
                  value={captionDraft}
                  onChange={(event) => setCaptionDraft(event.target.value)}
                />
                <ButtonV4
                  variant="ghost"
                  size="sm"
                  className="hv4-mt-8"
                  onClick={() =>
                    /* เก็บประเภทภาพไว้ครบ: description ที่ backend เก็บคือ "{ประเภท}: {คำอธิบาย}"
                       ช่องนี้แก้ได้เฉพาะคำอธิบาย (86629 ข้อ 4) */
                    dispatchGallery("UPDATE_PHOTO", {
                      photoId: editingPhoto.id,
                      description: galleryUploadDescription(editingPhoto.label, captionDraft),
                    })
                  }
                >
                  บันทึกคำอธิบาย
                </ButtonV4>
                <ButtonV4
                  variant="danger-ghost"
                  size="sm"
                  onClick={() => removePhoto(editingPhoto)}
                >
                  ลบภาพ
                </ButtonV4>
              </div>
            )}

            {readOnly && (
              <div className="hv4-modal-note">ดูอย่างเดียว (ภาพของ Encounter อื่น)</div>
            )}

            <div className="hv4-mt-12">
              <ButtonV4 variant="ok" disabled={readOnly} onClick={saveEditor}>
                บันทึกภาพที่แก้ไข
              </ButtonV4>
              {editingIndex !== null && (
                <span className="hv4-page-sub">
                  Encounter {encounterNumber ?? "-"} · {editingPhoto.caption}
                </span>
              )}
            </div>

            {/* สรุปผลของ layer ที่บันทึก/ลบ (86630) + error ที่เกิดจากปุ่มนี้ (canvas / upload) */}
            {live && examSlice?.gallerySavedMessage ? (
              <div className="hv4-save-msg hv4-mt-8" role="status">
                {examSlice.gallerySavedMessage}
              </div>
            ) : null}
            {localError !== null && (
              <AlertV4 tone="danger" className="hv4-mt-8" onClose={() => setLocalError(null)}>
                {localError}
              </AlertV4>
            )}
          </section>
        </div>
      )}
    </>
  );
};

export default CardGalleryV4;
